// MapLibre GL JS 5 wrapper shared by the Map, Territories, Store detail and Rep detail screens (WEB_PLAN.md §5.7,
// §5.8): one clustered GeoJSON source of store pins, optional territory polygons and rep markers, on the free
// OpenFreeMap vector style (Liberty; its Dark sibling in the dark theme, falling back to Liberty if that fails).
//
// Data in through props, events out through callbacks, camera moves through the ref. The map instance is created once
// per mount (center, zoom, interactive and showNavigation are creation options); later prop changes update the
// GeoJSON sources in place. Rebuilding is only needed when the set of pin colors changes, because MapLibre fixes
// `clusterProperties` (our per-color counters) when a source is created.
//
// Pins live on a WebGL canvas, so they are not focusable: screens that put the map in front of a keyboard user also
// list the same stores (the Stores table, the selection panel), and the canvas itself pans and zooms with the keys.
//
// Multi-select (the Map screen): `highlightedPointIds` draws a blue halo under those pins (a separate unclustered
// source, so a selection is visible at any zoom and changing it never re-clusters the pins), and `boxSelect` lets a
// drag draw a rectangle whose bounds go to `onBoxSelect`. The box is a plain DOM element moved on pointer events, so
// drawing it re-renders nothing in React.

import maplibregl from 'maplibre-gl';
import type { GeoJSONSource, MapGeoJSONFeature, MapLayerMouseEvent, MapMouseEvent, Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { MapPinOff, RefreshCw } from 'lucide-react';
import { useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode, type Ref } from 'react';
import { DEFAULT_MAP_CENTER, type Coordinate } from '@/domain/geo';
import { Button, EmptyState, Spinner, cn, useLatest, useResolvedTheme } from '@/ui';
import {
  clusterPalette,
  coordinateBounds,
  dataBounds,
  fitPadding,
  highlightCollection,
  markerCollection,
  paletteKey,
  pointCollection,
  polygonCollection,
  polygonLabelCollection,
  type MapBounds,
  type MapMarker,
  type MapPoint,
  type MapPolygon,
  type MarkerCollection,
  type PointCollection,
  type PolygonCollection,
  type PolygonLabelCollection,
} from './mapData';
import {
  LAYER,
  MAP_STYLE_DARK,
  MAP_STYLE_LIGHT,
  POINT_LAYER_IDS,
  SOURCE,
  highlightLayers,
  highlightSource,
  markerLayers,
  markerSource,
  pointLayers,
  pointSource,
  polygonLabelSource,
  polygonLayers,
  polygonSource,
  selectedFilter,
} from './mapLayers';

export type { MapLibreMap };

/** Zoom cap when fitting (a single store, or stores on one block, should not zoom to the doorstep). */
const FIT_MAX_ZOOM = 15;

const EMPTY_POINTS: readonly MapPoint[] = [];
const EMPTY_POLYGONS: readonly MapPolygon[] = [];
const EMPTY_MARKERS: readonly MapMarker[] = [];

/** A drag shorter than this (px, either side) is a click, not a box. */
const BOX_MIN_PX = 4;

/**
 * Rectangle selection: `off`; `shift` = Shift-drag draws a box (in place of MapLibre's Shift-drag box zoom); `drag` =
 * every one-finger or mouse drag draws a box, and panning is off until the mode changes (pinch and wheel still zoom).
 */
export type MapBoxSelect = 'off' | 'shift' | 'drag';

export interface MapClusterClick {
  clusterId: number;
  pointCount: number;
  coordinate: Coordinate;
  /** The zoom at which the cluster splits (the map is already easing there). */
  expansionZoom: number;
  /** Ids of every store in the cluster (e.g. to select them all). */
  leafIds(): Promise<string[]>;
}

export interface MapFitOptions {
  /** Pixels around the data (capped to a quarter of the map's shorter side). */
  padding?: number;
  maxZoom?: number;
  /** Ease instead of jumping (default true for calls through the ref). */
  animate?: boolean;
}

/** The imperative API (`ref`). Calls before the map exists are ignored. */
export interface MapCanvasHandle {
  /** Fit `[west, south, east, north]`, or the bounding box of some coordinates. */
  fitBounds(target: MapBounds | readonly (Coordinate | null | undefined)[], options?: MapFitOptions): void;
  /** Fit everything currently drawn (pins and territories; rep markers when there are neither). */
  fitToData(options?: MapFitOptions): void;
  /** Fly to a point; zoom defaults to at least street level (14). */
  flyTo(center: Coordinate, zoom?: number): void;
  resize(): void;
  /** The MapLibre map, for advanced screens (Terra Draw in the territory editor); null before mount or after failure. */
  getMap(): MapLibreMap | null;
}

export interface MapCanvasProps {
  /** Store pins. Invalid or (0,0) coordinates are skipped. Memoize the array: a new array re-uploads the source. */
  points?: readonly MapPoint[];
  /** Cluster pins (radius 50, until zoom 14; bubble = most common member color). Default true. */
  cluster?: boolean;
  /** Territory shapes (fill 0.18, outline, label at the centroid). */
  polygons?: readonly MapPolygon[];
  /** Rep positions (presence-colored discs with initials; `title` shows on hover). */
  markers?: readonly MapMarker[];
  /** Draws a ring around this pin (any id case). */
  selectedPointId?: string | null;
  /** Lower-case ids (`idSet`) drawn with a selection halo. Memoize the set: a new set re-uploads the halo source. */
  highlightedPointIds?: ReadonlySet<string> | null;
  /** Rectangle selection mode (default `off`). Needs `interactive`. */
  boxSelect?: MapBoxSelect;
  /** A box was drawn: `[west, south, east, north]` (west/east may pass ±180 on world copies). */
  onBoxSelect?: (bounds: MapBounds) => void;
  /** Fit the camera once, when the first non-empty data arrives. Default true. */
  fitOnFirstData?: boolean;
  /** Padding for fits, px. Default 48. */
  padding?: number;
  /** Start camera before any fit (creation option). Default: the Illinois–Indiana region iOS starts on. */
  initialCenter?: Coordinate;
  initialZoom?: number;
  /** false = a static mini-map (detail headers): no pan, zoom or controls (creation option). */
  interactive?: boolean;
  /** Zoom buttons, top right (creation option). Default true when interactive. */
  showNavigation?: boolean;
  /** Accessible name of the map ("Map of 1,240 stores"). */
  ariaLabel: string;
  onPointClick?: (id: string, info: { coordinate: Coordinate }) => void;
  /** Called after the map starts zooming into the cluster. */
  onClusterClick?: (cluster: MapClusterClick) => void;
  onPolygonClick?: (id: string) => void;
  onMarkerClick?: (id: string) => void;
  /** Once, when the first style has loaded and our layers are on it. */
  onMapReady?: (map: MapLibreMap) => void;
  styleUrl?: string;
  darkStyleUrl?: string;
  /** Size comes from the parent: give the wrapper (or this) a height. */
  className?: string;
  /** Overlays (legend, toolbar), positioned by the caller inside the map's box. */
  children?: ReactNode;
  ref?: Ref<MapCanvasHandle>;
}

/** Everything the imperative MapLibre side reads; kept in one ref so handlers registered once see fresh props. */
interface Snapshot {
  pointsData: PointCollection;
  highlightsData: PointCollection;
  palette: readonly string[];
  polygonsData: PolygonCollection;
  labelsData: PolygonLabelCollection;
  markersData: MarkerCollection;
  cluster: boolean;
  selectedPointId: string | null;
  dark: boolean;
  padding: number;
  fitOnFirstData: boolean;
  styleUrl: string;
  darkStyleUrl: string;
  onPointClick: MapCanvasProps['onPointClick'];
  onClusterClick: MapCanvasProps['onClusterClick'];
  onPolygonClick: MapCanvasProps['onPolygonClick'];
  onMarkerClick: MapCanvasProps['onMarkerClick'];
  onMapReady: MapCanvasProps['onMapReady'];
  boxSelect: MapBoxSelect;
  onBoxSelect: MapCanvasProps['onBoxSelect'];
}

type Failure = 'webgl' | 'style' | null;

// MARK: - Imperative helpers (outside React; they only touch the map)

function pointsKey(s: Pick<Snapshot, 'cluster' | 'palette'>): string {
  return `${s.cluster ? 'cluster' : 'plain'}|${paletteKey(s.palette)}`;
}

/** The base style's first label layer, so territory fills go under street and place names. */
function firstSymbolLayerId(map: MapLibreMap): string | undefined {
  return map.getStyle().layers?.find((l) => l.type === 'symbol' && !l.id.startsWith('gt-'))?.id;
}

/** Adds the pin source and layers, or just swaps the data when the cluster setup is unchanged. */
function installPoints(map: MapLibreMap, s: Snapshot, installed: { current: string | null }): void {
  const key = pointsKey(s);
  const existing = map.getSource<GeoJSONSource>(SOURCE.points);
  if (existing && installed.current === key) {
    existing.setData(s.pointsData);
    return;
  }
  for (const id of POINT_LAYER_IDS) if (map.getLayer(id)) map.removeLayer(id);
  if (existing) map.removeSource(SOURCE.points);
  map.addSource(SOURCE.points, pointSource(s.pointsData, { cluster: s.cluster, palette: s.palette }));
  const before = map.getLayer(LAYER.markers) ? LAYER.markers : undefined;
  for (const layer of pointLayers({ dark: s.dark }, { cluster: s.cluster, palette: s.palette, selectedId: s.selectedPointId })) {
    map.addLayer(layer, before);
  }
  installed.current = key;
}

/** Puts every source and layer on a freshly loaded style (first load, and after a light/dark style swap). */
function installAll(map: MapLibreMap, s: Snapshot, installed: { current: string | null }): void {
  const theme = { dark: s.dark };
  installed.current = null;
  if (!map.getSource(SOURCE.polygons)) map.addSource(SOURCE.polygons, polygonSource(s.polygonsData));
  if (!map.getSource(SOURCE.polygonLabels)) map.addSource(SOURCE.polygonLabels, polygonLabelSource(s.labelsData));
  const underLabels = firstSymbolLayerId(map);
  for (const layer of polygonLayers(theme)) {
    if (map.getLayer(layer.id)) continue;
    // Fills and outlines under the base map's labels; our territory names on top of everything of the base map.
    map.addLayer(layer, layer.id === LAYER.polygonLabel ? undefined : underLabels);
  }
  // Halos before the pins: a pin source rebuild re-adds its layers under the markers, so they stay above the halos.
  if (!map.getSource(SOURCE.highlights)) map.addSource(SOURCE.highlights, highlightSource(s.highlightsData));
  for (const layer of highlightLayers(theme)) if (!map.getLayer(layer.id)) map.addLayer(layer);
  installPoints(map, s, installed);
  if (!map.getSource(SOURCE.markers)) map.addSource(SOURCE.markers, markerSource(s.markersData));
  for (const layer of markerLayers(theme)) if (!map.getLayer(layer.id)) map.addLayer(layer);
}

function fitTo(map: MapLibreMap, bounds: MapBounds, options: { padding: number; maxZoom?: number; animate: boolean }): void {
  const box = map.getContainer();
  const padding = fitPadding(options.padding, box.clientWidth, box.clientHeight);
  map.fitBounds(bounds, { padding, maxZoom: options.maxZoom ?? FIT_MAX_ZOOM, animate: options.animate, duration: options.animate ? 600 : 0 });
}

/** What a fit covers: pins and territories, else rep markers. */
function snapshotBounds(s: Snapshot): MapBounds | undefined {
  return dataBounds({ points: s.pointsData, polygons: s.polygonsData }) ?? dataBounds({ markers: s.markersData });
}

function featureId(f: MapGeoJSONFeature | undefined): string | undefined {
  const id: unknown = f?.properties?.id;
  return typeof id === 'string' ? id : undefined;
}

function featureCoordinate(f: MapGeoJSONFeature): Coordinate | undefined {
  if (f.geometry.type !== 'Point') return undefined;
  const [longitude, latitude] = f.geometry.coordinates;
  return typeof longitude === 'number' && typeof latitude === 'number' ? { latitude, longitude } : undefined;
}

/** The cursor over the bare map: a crosshair while every drag draws a box, else MapLibre's own (grab). */
function idleCursor(s: Pick<Snapshot, 'boxSelect'>): string {
  return s.boxSelect === 'drag' ? 'crosshair' : '';
}

/**
 * Draws selection boxes on the map (`boxSelect`). Pointer events arrive before MapLibre's mouse and touch handlers,
 * so a starting box turns panning off before the pan could begin; a second finger cancels the box (it is a pinch).
 * Default actions are left alone: MapLibre still sees the mousedown, which keeps a long drag from counting as a click.
 */
function attachBoxSelect(map: MapLibreMap, latest: { readonly current: Snapshot }): () => void {
  const area = map.getCanvasContainer();
  const box = document.createElement('div');
  Object.assign(box.style, {
    position: 'absolute',
    left: '0',
    top: '0',
    display: 'none',
    pointerEvents: 'none',
    border: '1.5px solid #2673E6',
    background: 'rgb(38 115 230 / 0.12)',
    borderRadius: '2px',
    zIndex: '2',
  });
  area.appendChild(box);

  let drag: { pointerId: number; x0: number; y0: number; x: number; y: number; resumePan: boolean; userSelect: string } | null = null;

  const local = (e: PointerEvent) => {
    const r = area.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const draw = () => {
    if (!drag) return;
    const left = Math.min(drag.x0, drag.x);
    const top = Math.min(drag.y0, drag.y);
    box.style.transform = `translate(${left}px, ${top}px)`;
    box.style.width = `${Math.abs(drag.x - drag.x0)}px`;
    box.style.height = `${Math.abs(drag.y - drag.y0)}px`;
  };
  const stop = () => {
    if (!drag) return;
    if (drag.resumePan && latest.current.boxSelect !== 'drag') map.dragPan.enable();
    document.documentElement.style.userSelect = drag.userSelect;
    box.style.display = 'none';
    drag = null;
  };

  const onDown = (e: PointerEvent) => {
    if (drag) {
      stop(); // a second finger: the person is pinching, not drawing
      return;
    }
    const mode = latest.current.boxSelect;
    if (mode === 'off' || !e.isPrimary || e.button !== 0) return;
    if (mode === 'shift' && !e.shiftKey) return;
    const p = local(e);
    const resumePan = map.dragPan.isEnabled();
    if (resumePan) map.dragPan.disable();
    drag = { pointerId: e.pointerId, x0: p.x, y0: p.y, x: p.x, y: p.y, resumePan, userSelect: document.documentElement.style.userSelect };
    // No text selection in the overlays while the pointer sweeps across them.
    document.documentElement.style.userSelect = 'none';
    try {
      area.setPointerCapture(e.pointerId);
    } catch {
      // Not capturable (synthetic events): moves outside the map just stop updating the box.
    }
    draw();
    box.style.display = 'block';
  };
  const onMove = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const p = local(e);
    drag.x = p.x;
    drag.y = p.y;
    draw();
  };
  const onUp = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.pointerId) return;
    const { x0, y0 } = drag;
    const { x, y } = local(e);
    stop();
    if (Math.abs(x - x0) < BOX_MIN_PX || Math.abs(y - y0) < BOX_MIN_PX) return; // a click: pins handle it
    const a = map.unproject([x0, y0]);
    const b = map.unproject([x, y]);
    latest.current.onBoxSelect?.([Math.min(a.lng, b.lng), Math.min(a.lat, b.lat), Math.max(a.lng, b.lng), Math.max(a.lat, b.lat)]);
  };

  area.addEventListener('pointerdown', onDown);
  area.addEventListener('pointermove', onMove);
  area.addEventListener('pointerup', onUp);
  area.addEventListener('pointercancel', stop);
  return () => {
    stop();
    area.removeEventListener('pointerdown', onDown);
    area.removeEventListener('pointermove', onMove);
    area.removeEventListener('pointerup', onUp);
    area.removeEventListener('pointercancel', stop);
    box.remove();
  };
}

// MARK: - Component

export function MapCanvas({
  points = EMPTY_POINTS,
  cluster = true,
  polygons = EMPTY_POLYGONS,
  markers = EMPTY_MARKERS,
  selectedPointId = null,
  highlightedPointIds = null,
  boxSelect = 'off',
  onBoxSelect,
  fitOnFirstData = true,
  padding = 48,
  initialCenter = DEFAULT_MAP_CENTER,
  initialZoom = 6,
  interactive = true,
  showNavigation = true,
  ariaLabel,
  onPointClick,
  onClusterClick,
  onPolygonClick,
  onMarkerClick,
  onMapReady,
  styleUrl = MAP_STYLE_LIGHT,
  darkStyleUrl = MAP_STYLE_DARK,
  className,
  children,
  ref,
}: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  /** Our sources and layers are on the current style (false while a style loads). */
  const styleReadyRef = useRef(false);
  /** The map fired `load` (camera fits are safe). */
  const loadedRef = useRef(false);
  const installedPointsRef = useRef<string | null>(null);
  const fittedRef = useRef(false);
  /** The style URL currently loading or loaded. */
  const styleUrlRef = useRef('');
  /** The dark style failed once; stay on the light one for this mount. */
  const fellBackRef = useRef(false);
  /** The `boxSelect` mode the map's handlers are set for (a new map starts with MapLibre's defaults, i.e. `off`). */
  const appliedBoxModeRef = useRef<MapBoxSelect>('off');
  const [loaded, setLoaded] = useState(false);
  const [failure, setFailure] = useState<Failure>(null);
  const dark = useResolvedTheme() === 'dark';

  // GeoJSON is rebuilt only when the caller's arrays change (memoize them upstream).
  const pointsData = useMemo(() => pointCollection(points), [points]);
  const highlightsData = useMemo(() => highlightCollection(pointsData, highlightedPointIds), [pointsData, highlightedPointIds]);
  const palette = useMemo(() => (cluster ? clusterPalette(pointsData) : []), [pointsData, cluster]);
  const polygonsData = useMemo(() => polygonCollection(polygons), [polygons]);
  const labelsData = useMemo(() => polygonLabelCollection(polygons), [polygons]);
  const markersData = useMemo(() => markerCollection(markers), [markers]);

  const latest = useLatest<Snapshot>({
    pointsData,
    highlightsData,
    palette,
    polygonsData,
    labelsData,
    markersData,
    cluster,
    selectedPointId,
    dark,
    padding,
    fitOnFirstData,
    styleUrl,
    darkStyleUrl,
    onPointClick,
    onClusterClick,
    onPolygonClick,
    onMarkerClick,
    onMapReady,
    boxSelect: interactive ? boxSelect : 'off',
    onBoxSelect,
  });

  /** Fits once, on the first data there is (after `load`, so the container has its size). */
  const maybeFit = () => {
    const map = mapRef.current;
    const s = latest.current;
    if (!map || !loadedRef.current || fittedRef.current || !s.fitOnFirstData) return;
    const bounds = snapshotBounds(s);
    if (!bounds) return;
    fittedRef.current = true;
    fitTo(map, bounds, { padding: s.padding, animate: false });
  };

  // Create the map once per mount (creation options are read here only).
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const s0 = latest.current;
    const firstStyle = s0.dark ? s0.darkStyleUrl : s0.styleUrl;
    let map: MapLibreMap;
    try {
      map = new maplibregl.Map({
        container,
        style: firstStyle,
        center: [initialCenter.longitude, initialCenter.latitude],
        zoom: initialZoom,
        interactive,
        attributionControl: { compact: true },
        // A sales map is read top-down: no tilt or rotation to get lost in.
        dragRotate: false,
        pitchWithRotate: false,
        touchPitch: false,
        maxPitch: 0,
      });
    } catch {
      // MapLibre throws when the browser cannot create a WebGL context (disabled, blocked, very old GPU).
      setFailure('webgl');
      return;
    }
    mapRef.current = map;
    styleUrlRef.current = firstStyle;
    if (interactive) {
      map.touchZoomRotate.disableRotation();
      map.keyboard.disableRotation();
      if (showNavigation) map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    }

    const tip = new maplibregl.Popup({ closeButton: false, closeOnClick: false, offset: 16, maxWidth: '260px' });

    map.on('style.load', () => {
      installAll(map, latest.current, installedPointsRef);
      styleReadyRef.current = true;
      setFailure((f) => (f === 'style' ? null : f));
    });
    map.on('load', () => {
      loadedRef.current = true;
      setLoaded(true);
      maybeFit();
      latest.current.onMapReady?.(map);
    });
    map.on('error', () => {
      // After a style is up, errors are tile hiccups MapLibre retries on its own; before, the style itself failed.
      if (styleReadyRef.current) return;
      const s = latest.current;
      if (styleUrlRef.current === s.darkStyleUrl && s.darkStyleUrl !== s.styleUrl && !fellBackRef.current) {
        fellBackRef.current = true;
        styleUrlRef.current = s.styleUrl;
        map.setStyle(s.styleUrl, { diff: false });
        return;
      }
      setFailure('style');
    });

    map.on('click', LAYER.points, (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      const id = featureId(f);
      const coordinate = f ? featureCoordinate(f) : undefined;
      if (id && coordinate) latest.current.onPointClick?.(id, { coordinate });
    });

    map.on('click', LAYER.clusters, (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      const coordinate = f ? featureCoordinate(f) : undefined;
      const source = map.getSource<GeoJSONSource>(SOURCE.points);
      if (!f || !coordinate || !source) return;
      const clusterId = Number(f.properties?.cluster_id);
      const pointCount = Number(f.properties?.point_count ?? 0);
      void source
        .getClusterExpansionZoom(clusterId)
        .catch(() => map.getZoom() + 2)
        .then((expansionZoom) => {
          if (mapRef.current !== map) return; // unmounted while the worker answered
          map.easeTo({ center: [coordinate.longitude, coordinate.latitude], zoom: Math.min(expansionZoom, map.getMaxZoom()) });
          latest.current.onClusterClick?.({
            clusterId,
            pointCount,
            coordinate,
            expansionZoom,
            leafIds: async () => {
              const leaves = await source.getClusterLeaves(clusterId, Math.max(1, pointCount), 0);
              return leaves.map((l) => l.properties?.id).filter((id): id is string => typeof id === 'string');
            },
          });
        });
    });

    map.on('click', LAYER.polygonFill, (e: MapLayerMouseEvent) => {
      const handler = latest.current.onPolygonClick;
      if (!handler) return;
      // A pin or marker over the territory takes the click.
      const above = [LAYER.points, LAYER.clusters, LAYER.markers].filter((id) => map.getLayer(id));
      if (above.length > 0 && map.queryRenderedFeatures(e.point, { layers: above }).length > 0) return;
      const id = featureId(e.features?.[0]);
      if (id) handler(id);
    });

    map.on('click', LAYER.markers, (e: MapLayerMouseEvent) => {
      const id = featureId(e.features?.[0]);
      if (id) latest.current.onMarkerClick?.(id);
    });

    // One query per mouse move: pointer cursor over anything clickable, and the rep name tooltip over markers.
    map.on('mousemove', (e: MapMouseEvent) => {
      if (!styleReadyRef.current) return;
      const s = latest.current;
      const clickable = new Set<string>([LAYER.clusters]);
      if (s.onPointClick) clickable.add(LAYER.points);
      if (s.onPolygonClick) clickable.add(LAYER.polygonFill);
      if (s.onMarkerClick) clickable.add(LAYER.markers);
      const layers = [...clickable, LAYER.markers].filter((id, i, all) => all.indexOf(id) === i && map.getLayer(id));
      const hits = layers.length > 0 ? map.queryRenderedFeatures(e.point, { layers }) : [];
      map.getCanvas().style.cursor = hits.some((h) => clickable.has(h.layer.id)) ? 'pointer' : idleCursor(s);
      const marker = hits.find((h) => h.layer.id === LAYER.markers);
      const title: unknown = marker?.properties?.title;
      const at = marker ? featureCoordinate(marker) : undefined;
      if (typeof title === 'string' && title !== '' && at) {
        tip.setLngLat([at.longitude, at.latitude]).setText(title);
        if (!tip.isOpen()) tip.addTo(map);
      } else if (tip.isOpen()) {
        tip.remove();
      }
    });
    map.on('mouseout', () => {
      map.getCanvas().style.cursor = idleCursor(latest.current);
      tip.remove();
    });

    const detachBoxSelect = interactive ? attachBoxSelect(map, latest) : () => {};

    // The rail collapsing or a side panel opening resizes the box without a window resize.
    let frame = 0;
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => map.resize());
          });
    observer?.observe(container);

    return () => {
      observer?.disconnect();
      cancelAnimationFrame(frame);
      detachBoxSelect();
      tip.remove();
      map.remove();
      mapRef.current = null;
      styleReadyRef.current = false;
      loadedRef.current = false;
      installedPointsRef.current = null;
      fittedRef.current = false;
      appliedBoxModeRef.current = 'off';
      setLoaded(false);
    };
    // Creation options only: data and callbacks flow through `latest`, so this runs once per mount.
  }, []);

  // Accessible name on the canvas (MapLibre makes it the focusable region).
  useEffect(() => {
    mapRef.current?.getCanvas().setAttribute('aria-label', ariaLabel);
  }, [ariaLabel, loaded]);

  // Light/dark: swap the base style; `style.load` puts our layers back with the matching label colors.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const want = dark && !fellBackRef.current ? darkStyleUrl : styleUrl;
    if (want === styleUrlRef.current) return;
    styleUrlRef.current = want;
    styleReadyRef.current = false;
    map.setStyle(want, { diff: false });
  }, [dark, styleUrl, darkStyleUrl]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && styleReadyRef.current) installPoints(map, latest.current, installedPointsRef);
    maybeFit();
  }, [pointsData, palette, cluster]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && styleReadyRef.current) {
      map.getSource<GeoJSONSource>(SOURCE.polygons)?.setData(polygonsData);
      map.getSource<GeoJSONSource>(SOURCE.polygonLabels)?.setData(labelsData);
    }
    maybeFit();
  }, [polygonsData, labelsData]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && styleReadyRef.current) map.getSource<GeoJSONSource>(SOURCE.markers)?.setData(markersData);
    maybeFit();
  }, [markersData]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && styleReadyRef.current && map.getLayer(LAYER.selected)) map.setFilter(LAYER.selected, selectedFilter(selectedPointId));
  }, [selectedPointId]);

  useEffect(() => {
    const map = mapRef.current;
    if (map && styleReadyRef.current) map.getSource<GeoJSONSource>(SOURCE.highlights)?.setData(highlightsData);
  }, [highlightsData]);

  // Box selection takes over Shift-drag from MapLibre's box zoom, and in `drag` mode every drag (panning is off; the
  // touch-action keeps the browser from claiming one-finger drags once MapLibre's pan class is gone). Only a change of
  // mode touches the handlers, so screens that never use boxes keep whatever they set through `getMap()`.
  const boxMode: MapBoxSelect = interactive ? boxSelect : 'off';
  useEffect(() => {
    const map = mapRef.current;
    if (!map || appliedBoxModeRef.current === boxMode) return;
    appliedBoxModeRef.current = boxMode;
    if (boxMode === 'off') map.boxZoom.enable();
    else map.boxZoom.disable();
    if (boxMode === 'drag') map.dragPan.disable();
    else map.dragPan.enable();
    map.getCanvasContainer().style.touchAction = boxMode === 'drag' ? 'none' : '';
    map.getCanvas().style.cursor = idleCursor({ boxSelect: boxMode });
  }, [boxMode, loaded]);

  useImperativeHandle(
    ref,
    (): MapCanvasHandle => ({
      fitBounds(target, options) {
        const map = mapRef.current;
        if (!map) return;
        const bounds = typeof target[0] === 'number' ? (target as MapBounds) : coordinateBounds(target as readonly (Coordinate | null | undefined)[]);
        if (bounds) fitTo(map, bounds, { padding: options?.padding ?? latest.current.padding, maxZoom: options?.maxZoom, animate: options?.animate ?? true });
      },
      fitToData(options) {
        const map = mapRef.current;
        const bounds = snapshotBounds(latest.current);
        if (map && bounds) fitTo(map, bounds, { padding: options?.padding ?? latest.current.padding, maxZoom: options?.maxZoom, animate: options?.animate ?? true });
      },
      flyTo(center, zoom) {
        const map = mapRef.current;
        if (!map) return;
        map.flyTo({ center: [center.longitude, center.latitude], zoom: zoom ?? Math.max(map.getZoom(), 14), essential: true });
      },
      resize() {
        mapRef.current?.resize();
      },
      getMap() {
        return mapRef.current;
      },
    }),
    [latest],
  );

  const retryStyle = () => {
    const map = mapRef.current;
    if (!map) return;
    setFailure(null);
    styleReadyRef.current = false;
    map.setStyle(styleUrlRef.current, { diff: false });
  };

  return (
    <div className={cn('relative h-full min-h-[200px] w-full overflow-hidden bg-surface-2', className)}>
      {/* Inline position, not Tailwind's `absolute inset-0`: maplibre-gl.css (unlayered) sets `.maplibregl-map {
          position: relative }`, which beats Tailwind 4's layered utilities and collapsed the map to 0 px high. */}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      {!loaded && failure === null && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-surface/90 px-3 py-1.5 text-sm text-fg-muted shadow-card">
            <Spinner size="sm" label={null} />
            Loading map…
          </span>
        </div>
      )}
      {failure !== null && (
        <div role="alert" className="absolute inset-0 flex items-center justify-center bg-surface-2/95 p-4">
          <EmptyState
            compact
            icon={<MapPinOff />}
            color="#8C919E"
            title={failure === 'webgl' ? "The map can't be shown in this browser" : "The map couldn't load"}
            message={
              failure === 'webgl'
                ? 'Drawing the map needs WebGL, which is turned off or not supported here. Everything else in the console still works.'
                : 'Check your connection and try again.'
            }
            action={
              failure === 'style' ? (
                <Button variant="secondary" icon={<RefreshCw />} onClick={retryStyle}>
                  Try again
                </Button>
              ) : undefined
            }
          />
        </div>
      )}
      {failure !== 'webgl' && children}
    </div>
  );
}
